import { capitalize, identity } from 'lodash';
import {
  asCadence,
  asStrictTruncatedText,
  asWarning,
  millisAsHours,
  numberAsCurrencyWithCents,
} from '../utils/formatters';
import { WidthLimitedEllipsedText } from '../utils/styles';
import { getOperationEquipmentType } from '../utils/operations';
import { formatOperationGroup } from './operations';

const OPS_BASE_COLS = [
  {
    accessor: 'name',
    displayString: 'Name',
  },
  {
    accessor: 'category',
    displayString: 'Category',
    format: (category) => capitalize(category),
  },
  {
    accessor: 'group',
    displayString: 'Group',
    format: formatOperationGroup,
  },
  {
    displayString: 'Operation Type',
    accessor: 'contracted',
    format: (contracted, op) => getOperationEquipmentType(op),
  },
];

export const OPS_COLS = [
  ...OPS_BASE_COLS,
  {
    accessor: 'displayEquipment',
    displayString: 'Equipment',
    format: asStrictTruncatedText,
  },
  {
    accessor: 'operationInputs',
    displayString: 'Inputs',
    format: (operationInputs) =>
      asStrictTruncatedText(
        (operationInputs || []).map(({ input }) => input.name).join(', '),
      ),
    style: WidthLimitedEllipsedText,
  },
];

export const OPS_SUPERADMIN_COLS = [
  ...OPS_COLS,
  {
    accessor: 'valid',
    displayString: 'Valid',
    format: asWarning,
  },
];

export const operationCsvCols = (unitsMap) => [
  ...OPS_BASE_COLS,
  {
    displayString: 'Assignment',
    accessor: 'assignment',
    format: (assignment) => capitalize(assignment),
  },
  {
    displayString: 'Cost per unit',
    accessor: 'dollarsPerUnit',
    format: (dollars, op) => {
      const transformToUnit =
        unitsMap[op.unitForDollars]?.transformToUnit || identity;
      return numberAsCurrencyWithCents(transformToUnit(dollars));
    },
  },
  {
    displayString: 'Cost unit',
    accessor: 'unitForDollars',
    format: (unit) => unitsMap[unit]?.displayCsv || unit,
  },
  {
    displayString: 'Rental cost per day',
    accessor: 'equipmentRentalCostPerDay',
    format: numberAsCurrencyWithCents,
  },
  {
    displayString: 'Duration per unit (hr)',
    accessor: 'millisecondsPerUnit',
    format: (milliseconds, op) => {
      const transformToUnit =
        unitsMap[op.unitForMilliseconds]?.transformToUnit || identity;
      return millisAsHours(transformToUnit(milliseconds), 4);
    },
  },
  {
    displayString: 'Duration unit',
    accessor: 'unitForMilliseconds',
    format: (unit) => unitsMap[unit]?.displayCsv || unit,
  },
  {
    displayString: 'Duration overrides (hr)',
    accessor: 'millisecondsOverrides',
    format: (overrides, op) => {
      if (!Object.keys(overrides || {}).length) return undefined;
      const transformToUnit =
        unitsMap[op.unitForMilliseconds]?.transformToUnit || identity;
      return Object.entries(overrides)
        .map(([yr, o]) => `Year ${yr}: ${millisAsHours(transformToUnit(o))}`, 4)
        .join(', ');
    },
  },
  {
    displayString: 'Laborers',
    accessor: 'numberOfLaborers',
  },
  {
    displayString: 'Equipment',
    accessor: 'displayEquipment',
  },
  {
    displayString: 'Allocation to loss events',
    accessor: 'lossEvents',
    format: (lossEvents) => lossEvents?.map((le) => le.name).join(', '),
  },
  {
    displayString: 'Allocation to revenue channels',
    accessor: 'revenueStreams',
    format: (revStreams) => revStreams?.map((rs) => rs.name).join(', '),
  },
  {
    displayString: 'Cadence',
    accessor: 'cadence',
    format: asCadence,
  },
  {
    displayString: 'Start year',
    accessor: 'startingYear',
  },
  {
    displayString: 'End year',
    accessor: 'endingYear',
  },
  {
    displayString: 'Times per year',
    accessor: 'timesPerYear',
  },
  {
    displayString: 'Custom years',
    accessor: 'years',
    format: (years) => years?.join(', '),
  },
];

export const operationInputCsvCols = (numOpInputs, unitsMap) =>
  [...Array(numOpInputs).keys()].flatMap((i) => {
    return [
      {
        displayString: `Input ${i + 1}`,
        accessor: (op) => op.operationInputs[i]?.input?.name,
      },
      // N.B. Input price and associated unit do not update for metric
      {
        displayString: 'Price per unit',
        accessor: (op) => op.operationInputs[i]?.input?.unitCost,
        format: numberAsCurrencyWithCents,
      },
      {
        displayString: 'Unit',
        accessor: (op) => op.operationInputs[i]?.input?.unit,
      },
      {
        displayString: 'Quantity per unit',
        accessor: (op) => op.operationInputs[i]?.amountPerUnit,
        format: (qty, op) => {
          const transformToUnit =
            unitsMap[op.operationInputs[i]?.unitForAmount]?.transformToUnit ||
            identity;
          return transformToUnit(qty);
        },
      },
      {
        displayString: 'Unit',
        accessor: (op) => op.operationInputs[i]?.unitForAmount,
        format: (unit) => unitsMap[unit]?.displayCsv || unit,
      },
    ];
  });
